import React from 'react';
import { motion } from 'motion/react';
import { Clock, MapPin } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent, ScheduleItem } from '../content';

function TimelineNode({ item, index, total }: { item: ScheduleItem; index: number; total: number; key?: React.Key }) {
  const isLast = index === total - 1;

  return (
    <motion.div
      initial={{ opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.65, delay: index * 0.12, ease: BDAY_EASE }}
      className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} md:flex-row`}
    >
      {/* connector line */}
      {!isLast && (
        <div className="absolute left-[calc(50%-1px)] md:left-6 top-14 w-0.5 h-full bg-gradient-to-b from-blue-400/60 to-blue-800/20 -z-0" />
      )}

      {/* node dot */}
      <div className="relative flex-shrink-0 z-10">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 flex items-center justify-center text-xl shadow-lg shadow-blue-900/60 border-2 border-blue-300/30">
          {item.emoji ?? '⭐'}
        </div>
        <motion.div
          animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.4 }}
          className="absolute inset-0 rounded-full bg-blue-400/40"
        />
      </div>

      {/* card */}
      <motion.div
        whileHover={{ scale: 1.02, y: -2 }}
        transition={{ duration: 0.3 }}
        className="flex-1 mb-8 bg-white/5 backdrop-blur-sm border border-blue-400/20 rounded-2xl p-5 shadow-lg shadow-blue-900/30"
      >
        <div className="flex items-center gap-2 mb-1">
          <Clock size={13} className="text-blue-400" />
          <span className="text-blue-300 text-xs font-bold uppercase tracking-wider">{item.time}</span>
        </div>
        <h3 className="text-white font-bold text-lg mb-1">{item.activity}</h3>
        {item.description && (
          <p className="text-blue-200 text-sm leading-relaxed mb-2">{item.description}</p>
        )}
        {item.venue && (
          <div className="flex items-center gap-1.5 text-blue-400 text-xs">
            <MapPin size={12} />
            {item.venue}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export function ScheduleSection({ content }: { content: BirthdayContent['schedule'] }) {
  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #1e3a5f 0%, #0f172a 100%)' }}
    >
      <div className="max-w-2xl mx-auto">
        {/* heading */}
        <div className="text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            {content.label}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            {content.title}
          </motion.h2>
        </div>

        {/* timeline */}
        <div className="relative flex flex-col gap-2">
          {content.items.map((item, i) => (
            <TimelineNode key={`${item.time}-${i}`} item={item} index={i} total={content.items.length} />
          ))}
        </div>
      </div>
    </section>
  );
}
